// src/AudioSpectrum/effects/dancingButterfly.js
import { getHSLColor } from '../colorUtils.js';

export const drawDancingButterfly = (ctx, frequencyData, width, height, butterflyRef) => {
  // 배경 - 아름다운 꽃밭 하늘
  const gradient = ctx.createLinearGradient(0, 0, 0, height);
  gradient.addColorStop(0, 'rgba(135, 206, 235, 0.1)'); // 하늘색
  gradient.addColorStop(0.7, 'rgba(152, 251, 152, 0.05)'); // 연한 초록
  gradient.addColorStop(1, 'rgba(34, 139, 34, 0.1)'); // 짙은 초록
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, width, height);
  
  // 땅 부분
  const groundY = height * 0.75;
  
  // 초기화: 나비들과 꽃들이 없으면 생성
  if (!butterflyRef.current.butterflies) {
    butterflyRef.current.butterflies = [];
    butterflyRef.current.flowers = [];
    butterflyRef.current.sparkles = [];
    butterflyRef.current.windParticles = [];
  }
  
  // 배경 꽃들 생성 (한 번만)
  if (butterflyRef.current.flowers.length === 0) {
    const flowerCount = 700;
    for (let i = 0; i < flowerCount; i++) {
      butterflyRef.current.flowers.push({
        x: Math.random() * width,
        y: groundY - Math.random() * 40 + Math.random() * 20,
        size: Math.random() * 4 + 2,
        color: Math.random() < 0.4 ? 'white' : (Math.random() < 0.5 ? 'pink' : 'purple'),
        swayPhase: Math.random() * Math.PI * 2,
        swaySpeed: Math.random() * 0.02 + 0.01
      });
    }
  }
  
  // 나비들 생성 (한 번만)
  if (butterflyRef.current.butterflies.length === 0) {
    const butterflyCount = 5;
    for (let i = 0; i < butterflyCount; i++) {
      butterflyRef.current.butterflies.push({
        x: Math.random() * width,
        y: groundY - Math.random() * 80 - 15,
        vx: (Math.random() - 0.5) * 2,
        vy: (Math.random() - 0.5) * 1,
        baseY: groundY - Math.random() * 80 - 15,
        size: Math.random() * 4 + 6,
        color: Math.random() < 0.6 ? 'white' : 'yellow',
        wingPhase: Math.random() * Math.PI * 2,
        wingSpeed: Math.random() * 0.3 + 0.2,
        flightPhase: Math.random() * Math.PI * 2,
        flightRadius: Math.random() * 30 + 20,
        freqBand: Math.floor(Math.random() * Math.min(frequencyData.length, 200)),
        isActive: false,
        activationThreshold: Math.random() * 40 + 25,
        energy: 0,
        targetX: Math.random() * width,
        targetY: groundY - Math.random() * 150 - 50,
        pathChangeTimer: 0,
        danceIntensity: 0
      });
    }
  }
  
  // 주파수 데이터 분석
  const avgFreq = frequencyData.reduce((a, b) => a + b, 0) / frequencyData.length;
  const bassAvg = frequencyData.slice(0, 30).reduce((a, b) => a + b, 0) / 30;
  const midAvg = frequencyData.slice(30, 120).reduce((a, b) => a + b, 0) / 90;
  const highAvg = frequencyData.slice(120, 250).reduce((a, b) => a + b, 0) / 130;
  
  // 음악에 반응하는 반짝임 효과 생성
  if (avgFreq > 30 && Math.random() < 0.4) {
    const sparkleCount = Math.floor((avgFreq / 255) * 5) + 1;
    for (let i = 0; i < sparkleCount; i++) {
      butterflyRef.current.sparkles.push({
        x: Math.random() * width,
        y: Math.random() * (groundY - 100) + 50,
        life: 1.0,
        decay: 0.015,
        size: Math.random() * 3 + 1,
        twinkleSpeed: Math.random() * 0.1 + 0.05,
        brightness: Math.random() * 0.6 + 0.4
      });
    }
  }
  
  // 바람 파티클 생성 (부드러운 움직임을 위해)
  if (Math.random() < 0.3) {
    butterflyRef.current.windParticles.push({
      x: -10,
      y: Math.random() * height * 0.6,
      vx: Math.random() * 1 + 0.5,
      vy: (Math.random() - 0.5) * 0.5,
      life: 1.0,
      decay: 0.005,
      size: Math.random() * 2 + 1
    });
  }
  
  // 배경 꽃들 그리기
  butterflyRef.current.flowers.forEach(flower => {
    flower.swayPhase += flower.swaySpeed;
    const sway = Math.sin(flower.swayPhase) * 2;
    
    let flowerColor;
    switch(flower.color) {
      case 'white':
        flowerColor = getHSLColor(0, 0, 90);
        break;
      case 'pink':
        flowerColor = getHSLColor(320, 70, 75);
        break;
      case 'purple':
        flowerColor = getHSLColor(280, 60, 70);
        break;
    }
    
    // 꽃 줄기
    ctx.strokeStyle = getHSLColor(100, 40, 30);
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(flower.x, flower.y + 5);
    ctx.lineTo(flower.x + sway * 0.5, flower.y - flower.size);
    ctx.stroke();
    
    // 꽃잎들
    ctx.fillStyle = flowerColor;
    const petalCount = 5;
    for (let p = 0; p < petalCount; p++) {
      const angle = (p / petalCount) * Math.PI * 2;
      const petalX = flower.x + sway * 0.5 + Math.cos(angle) * flower.size * 0.6;
      const petalY = flower.y - flower.size + Math.sin(angle) * flower.size * 0.6;
      
      ctx.beginPath();
      ctx.arc(petalX, petalY, flower.size * 0.3, 0, Math.PI * 2);
      ctx.fill();
    }
    
    // 꽃 중심
    ctx.fillStyle = getHSLColor(50, 80, 70);
    ctx.beginPath();
    ctx.arc(flower.x + sway * 0.5, flower.y - flower.size, flower.size * 0.2, 0, Math.PI * 2);
    ctx.fill();
  });
  
  // 바람 파티클 업데이트 및 그리기
  butterflyRef.current.windParticles = butterflyRef.current.windParticles.filter(particle => {
    particle.x += particle.vx;
    particle.y += particle.vy;
    particle.life -= particle.decay;
    
    if (particle.life > 0 && particle.x < width + 20) {
      ctx.globalAlpha = particle.life * 0.3;
      ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
      ctx.beginPath();
      ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalAlpha = 1.0;
      return true;
    }
    return false;
  });
  
  // 반짝임 효과 업데이트 및 그리기
  butterflyRef.current.sparkles = butterflyRef.current.sparkles.filter(sparkle => {
    sparkle.life -= sparkle.decay;
    
    if (sparkle.life > 0) {
      const twinkle = Math.sin(Date.now() * sparkle.twinkleSpeed) * 0.5 + 0.5;
      ctx.globalAlpha = sparkle.life * sparkle.brightness * twinkle;
      ctx.fillStyle = 'rgba(255, 255, 200, 0.9)';
      ctx.beginPath();
      ctx.arc(sparkle.x, sparkle.y, sparkle.size, 0, Math.PI * 2);
      ctx.fill();
      
      // 별 모양 반짝임
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.8)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(sparkle.x - sparkle.size * 2, sparkle.y);
      ctx.lineTo(sparkle.x + sparkle.size * 2, sparkle.y);
      ctx.moveTo(sparkle.x, sparkle.y - sparkle.size * 2);
      ctx.lineTo(sparkle.x, sparkle.y + sparkle.size * 2);
      ctx.stroke();
      
      ctx.globalAlpha = 1.0;
      return true;
    }
    return false;
  });
  
  // 나비들 애니메이션 및 그리기
  butterflyRef.current.butterflies.forEach(butterfly => {
    // 주파수에 따른 활성화 여부 결정
    const freqValue = frequencyData[butterfly.freqBand] || 0;
    const intensity = freqValue / 255;
    
    // 나비의 활성화 상태 업데이트
    if (freqValue > butterfly.activationThreshold) {
      butterfly.isActive = true;
      butterfly.energy = Math.min(butterfly.energy + 0.05, 1);
    } else {
      butterfly.isActive = false;
      butterfly.energy = Math.max(butterfly.energy - 0.02, 0);
    }
    
    // 음악 강도에 따른 춤 강도 계산
    const bassInfluence = bassAvg / 255 * 0.3;
    const midInfluence = midAvg / 255 * 0.5;
    const highInfluence = highAvg / 255 * 0.4;
    
    butterfly.danceIntensity = (intensity + bassInfluence + midInfluence + highInfluence) * butterfly.energy;
    
    // 경로 변경 타이머
    butterfly.pathChangeTimer++;
    if (butterfly.pathChangeTimer > 180 || (butterfly.isActive && Math.random() < 0.02)) {
      butterfly.targetX = Math.random() * width;
      butterfly.targetY = groundY - Math.random() * 180 - 30;
      butterfly.pathChangeTimer = 0;
    }
    
    // 부드러운 움직임 (목표점을 향해 이동)
    const dx = butterfly.targetX - butterfly.x;
    const dy = butterfly.targetY - butterfly.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    
    if (distance > 5) {
      butterfly.vx += (dx / distance) * 0.03;
      butterfly.vy += (dy / distance) * 0.03;
    }
    
    // 음악에 반응하는 추가 움직임
    if (butterfly.isActive) {
      butterfly.flightPhase += 0.15 + butterfly.danceIntensity * 0.1;
      const danceRadius = butterfly.flightRadius * (0.5 + butterfly.danceIntensity);
      
      butterfly.vx += Math.sin(butterfly.flightPhase) * 0.8 * butterfly.danceIntensity;
      butterfly.vy += Math.cos(butterfly.flightPhase * 1.3) * 0.6 * butterfly.danceIntensity;
    } else {
      butterfly.flightPhase += 0.05;
    }
    
    // 속도 제한 및 마찰
    butterfly.vx *= 0.95;
    butterfly.vy *= 0.95;
    butterfly.vx = Math.max(-3, Math.min(3, butterfly.vx));
    butterfly.vy = Math.max(-2, Math.min(2, butterfly.vy));
    
    // 위치 업데이트
    butterfly.x += butterfly.vx;
    butterfly.y += butterfly.vy;
    
    // 경계 처리 (화면 밖으로 나가지 않게)
    if (butterfly.x < 0) {
      butterfly.x = 0;
      butterfly.vx *= -0.5;
    }
    if (butterfly.x > width) {
      butterfly.x = width;
      butterfly.vx *= -0.5;
    }
    if (butterfly.y < 20) {
      butterfly.y = 20;
      butterfly.vy *= -0.5;
    }
    if (butterfly.y > groundY - 10) {
      butterfly.y = groundY - 10;
      butterfly.vy *= -0.5;
    }
    
    // 날개 펄럭임
    butterfly.wingPhase += butterfly.wingSpeed + (butterfly.danceIntensity * 0.3);
    const wingBeat = Math.sin(butterfly.wingPhase) * 0.5 + 0.5;
    
    // 나비 크기 (음악에 반응해서 약간 커짐)
    const currentSize = butterfly.size + (butterfly.danceIntensity * 1.5);
    
    // 나비 색상 설정
    let butterflyColor, wingDetailColor;
    if (butterfly.color === 'white') {
      const brightness = 85 + (butterfly.danceIntensity * 15);
      butterflyColor = getHSLColor(0, 0, brightness);
      wingDetailColor = getHSLColor(240, 30, 60);
    } else { // yellow
      const saturation = 70 + (butterfly.danceIntensity * 20);
      const brightness = 65 + (butterfly.danceIntensity * 10);
      butterflyColor = getHSLColor(50, saturation, brightness);
      wingDetailColor = getHSLColor(30, 60, 50);
    }
    
    ctx.save();
    ctx.translate(butterfly.x, butterfly.y);
    
    // 나비의 향하는 방향에 따른 회전
    const moveAngle = Math.atan2(butterfly.vy, butterfly.vx);
    ctx.rotate(moveAngle * 0.3);
    
    // 나비 몸체
    ctx.fillStyle = getHSLColor(0, 0, 20);
    ctx.beginPath();
    ctx.ellipse(0, 0, currentSize * 0.6, currentSize * 0.12, 0, 0, Math.PI * 2);
    ctx.fill();
    
    // 나비 더듬이
    ctx.strokeStyle = getHSLColor(0, 0, 30);
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(-currentSize * 0.5, -currentSize * 0.08);
    ctx.lineTo(-currentSize * 0.6, -currentSize * 0.2);
    ctx.moveTo(-currentSize * 0.5, -currentSize * 0.08);
    ctx.lineTo(-currentSize * 0.3, -currentSize * 0.2);
    ctx.stroke();
    
    // 날개 그리기
    const wingScale = 0.7 + wingBeat * 0.3;
    const wingRotation = wingBeat * 0.3;
    
    // 왼쪽 날개들
    ctx.save();
    ctx.rotate(-wingRotation);
    ctx.scale(wingScale, 1);
    
    // 왼쪽 위 날개
    ctx.fillStyle = butterflyColor;
    ctx.beginPath();
    ctx.ellipse(-currentSize * 0.3, -currentSize * 0.4, currentSize * 0.6, currentSize * 0.4, -0.3, 0, Math.PI * 2);
    ctx.fill();
    
    // 왼쪽 아래 날개
    ctx.beginPath();
    ctx.ellipse(-currentSize * 0.15, currentSize * 0.15, currentSize * 0.3, currentSize * 0.25, 0.2, 0, Math.PI * 2);
    ctx.fill();
    
    ctx.restore();
    
    // 오른쪽 날개들
    ctx.save();
    ctx.rotate(wingRotation);
    ctx.scale(wingScale, 1);
    
    // 오른쪽 위 날개
    ctx.fillStyle = butterflyColor;
    ctx.beginPath();
    ctx.ellipse(currentSize * 0.25, -currentSize * 0.3, currentSize * 0.45, currentSize * 0.3, 0.3, 0, Math.PI * 2);
    ctx.fill();
    
    // 오른쪽 아래 날개
    ctx.beginPath();
    ctx.ellipse(currentSize * 0.15, currentSize * 0.15, currentSize * 0.3, currentSize * 0.25, -0.2, 0, Math.PI * 2);
    ctx.fill();
    
    ctx.restore();
    
    // 날개 장식 무늬
    if (butterfly.energy > 0.3) {
      ctx.fillStyle = wingDetailColor;
      
      // 왼쪽 날개 무늬
      ctx.beginPath();
      ctx.arc(-currentSize * 0.4, -currentSize * 0.3, currentSize * 0.1, 0, Math.PI * 2);
      ctx.fill();
      
      // 오른쪽 날개 무늬
      ctx.beginPath();
      ctx.arc(currentSize * 0.4, -currentSize * 0.3, currentSize * 0.1, 0, Math.PI * 2);
      ctx.fill();
    }
    
    // 강한 음악일 때 빛나는 효과
    if (butterfly.danceIntensity > 0.7) {
      ctx.globalAlpha = butterfly.danceIntensity * 0.4;
      ctx.fillStyle = 'rgba(255, 255, 255, 0.6)';
      ctx.beginPath();
      ctx.arc(0, 0, currentSize * 1.2, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalAlpha = 1.0;
      
      // 주변에 작은 별 효과
      const starCount = Math.floor(butterfly.danceIntensity * 4);
      for (let s = 0; s < starCount; s++) {
        const starAngle = (s / starCount) * Math.PI * 2 + Date.now() * 0.01;
        const starDistance = currentSize * 1.5;
        const starX = Math.cos(starAngle) * starDistance;
        const starY = Math.sin(starAngle) * starDistance;
        
        ctx.fillStyle = `rgba(255, 255, 200, ${Math.random() * 0.5})`;
        ctx.beginPath();
        ctx.arc(starX, starY, 1, 0, Math.PI * 2);
        ctx.fill();
      }
    }
    
    ctx.restore();
  });
};